import { captureMessage } from "src/api/sentry";
import { Memoizer } from "src/hooks/useMemoizer";
import { PageState } from "src/hooks/usePageState";
import { ItemControl } from "src/hooks/useSubpages";
import { Applicant, SharedItem } from "src/types/Applicant";

export function filterSharedItemsByApplicant<T extends SharedItem = object>(
  pageState: PageState<ItemControl<T> | undefined>,
  applicant: Applicant,
  memoizer: Memoizer<any>
): PageState<ItemControl<T> | undefined> {
  const obj = pageState.value;
  const items = obj?.items || [];

  const ownedIndices = items.map(
    (item) => (item.owners || []).findIndex((owner) => owner?.email?.email === applicant?.email?.email) !== -1
  );
  const ownedItems = memoizer(
    "ownedItems",
    [items, applicant],
    items.filter(
      (item) =>
        (item.owners || []).findIndex((owner) => owner?.email?.email === applicant?.email?.email) !== -1
    )
  );

  // This is the "translation" layer to translate changes to the "filtered" list to the full list.
  // Since we are only passing a subset of the items to the component that calls onChange,
  // we want to intercept that onChange call to include the full set of items,
  // so that other applicant's items (that aren't shared with this applicant) aren't
  // affected.
  const handleChange = memoizer(
    "handleChange",
    [ownedItems, applicant?.email?.email, obj],
    (change: ItemControl<T & { id?: string }>) => {
      const mainItemList: Array<{ id?: string } & T> = [...items];
      let editing = obj?.__editing;
      let existing = obj?.__existing;
      if (change.__lastCommand === "delete" && change.__target !== undefined) {
        const deletedItem = change.items[change.__target];
        // an item was deleted. we need to find it and then delete it from the main item list.
        let i = 0;
        for (i; i < mainItemList.length; i++) {
          if (mainItemList[i]?.id === deletedItem?.id || mainItemList[i] === deletedItem) {
            break;
          }
        }
        mainItemList.splice(i, 1);
        editing = editing !== undefined && editing >= i ? editing - 1 : editing;
        existing = undefined;
      } else if (change.__lastCommand === "add") {
        // an item was added. we can assume it was the last item added to the changes list,
        // so we add that into the main item list. Add in the applicant as the first owner
        const newItem = change.items[change.items.length - 1];
        mainItemList.push({ ...newItem, owners: [...(newItem.owners || []), applicant] });
        existing = undefined;
        editing = mainItemList.length - 1;
      } else if (change.__lastCommand === "edit" && change.__editing !== undefined) {
        // an item is set to be edited. we need to find the index in the main item list
        let i = 0;
        let found = false;
        for (i; i < mainItemList.length; i++) {
          if (
            // if there's an id, compare by that. Otherwise, compare raw items
            (mainItemList[i].id && mainItemList[i].id === change.items[change.__editing].id) ||
            mainItemList[i] === change.items[change.__editing]
          ) {
            found = true;
            break;
          }
        }
        if (found) {
          editing = i;
          existing = mainItemList[editing];
        } else {
          captureMessage("Unable to find item in main item list");
        }
      } else {
        // This is an unhandled situation
        captureMessage("Unhandled onChange scenario");
      }
      return pageState.onChange({
        ...change,
        items: mainItemList,
        __existing: existing,
        __editing: editing,
      });
    }
  );

  const previouslyEditedItem = obj?.__editing !== undefined ? items[obj.__editing] : undefined;
  let newEditingIndex: number | undefined = ownedItems.findIndex(
    (item: T) => JSON.stringify(previouslyEditedItem) === JSON.stringify(item)
  );
  newEditingIndex = newEditingIndex !== -1 ? newEditingIndex : undefined;

  const existing = ownedIndices[obj?.__editing !== undefined ? obj.__editing : -1]
    ? obj?.__existing
    : undefined;

  return {
    ...pageState,
    // @ts-ignore
    onChange: handleChange,
    value: memoizer("value", [pageState, applicant], {
      ...obj,
      __existing: existing, // don't show existing if its not owned by this applicant
      __editing: newEditingIndex,
      items: ownedItems,
    }),
  };
}
